/// <reference types="google.maps" />
import { Component, EventEmitter, Input, Output, ViewChild, ViewEncapsulation, inject, OnInit, AfterViewInit, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { UserPreferencesService } from '../../../../shared/services/user-preferences/user-preferences.service';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { FieldType, LogicAction } from '../../../../shared/enums/template';
import { Rule, createRuleEvaluator, extractRules } from '../../../../shared/utils/rule-expression';
import { CommonConfirmationDialog } from '../../../../shared/models/user-preferences.model';
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { DrawingPadComponent } from '../../../../shared/components/drawing-pad/drawing-pad.component';
import { MatDialog } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { CreateNoteComponent } from '../create-note/create-note.component';
import { CreateMediaComponent } from '../create-media/create-media.component';
import { LoaderService } from '../../../../shared/services/loader/loader.service';
import { GlobalService } from '../../../../shared/services/global/global-service';
import { ToastrService } from 'ngx-toastr';
import { Constants, UploadOperation } from '../../../../shared/constants/common';
import { FormatTitlePopupComponent } from '../format-title-popup/format-title-popup.component';
import { InspectionService } from '../../../../shared/services/inspection/inspection-service';
import { ToastService } from '../../../../shared/services/toast/toast.service';
import { CreateActionComponent } from '../../../../shared/components/create-action/create-action.component';
import moment from 'moment';
import { ActionStatus, ActionStatusID } from '../../../../shared/enums/action';
import { debounceTime, distinctUntilChanged, firstValueFrom, map, Observable, pairwise, startWith } from 'rxjs';
import { AppModuleName } from '../../../../shared/enums/linkModule';
import { UploadMediaRequest } from '../../../../shared/models/template.model';
import { LocalStorageService } from '../../../../shared/services/local-storage/localstorage.service';
import { UserPreferencesData } from '../../../../shared/models/user-preferences.model';
import { PermissionService } from '../../../../core/services/permission.service';
import { HttpErrorResponse } from '@angular/common/http';
import { LookupSelectDialogComponent, LookupSelectDialogData, LookupSelectOption } from '../../../../shared/components/lookup-select-dialog/lookup-select-dialog.component';
import { WorkOrderService } from '../../../../shared/services/work-order/work-order.service';

export interface TemplateLinkSummary {
  count: number;
  disabled?: boolean;
  loading?: boolean;
  label?: string;
}

interface ChecklistResponse {
  val: string | string[] | MediaFileInfo[] | null;
  note?: string;
  media?: MediaFileInfo[];
  action?: ActionInfo[];
}

interface MediaFileInfo {
  caption: string;
  data: string;
  url: string;
}

interface ActionInfo {
  id: number;
  caption: string;
  status: string;
}

interface TemplateDataItem {
  fieldType: string;
  itemCode: string;
  formattedItemCode?: string;
  caption?: string;
  ans?: string | string[] | null;
  note?: string;
  media?: MediaFileInfo[];
  action?: ActionInfo[];
  links?: LinkItem[];
  sections?: TemplateSection[];
  items?: TemplateItem[];
  sectionNo?: number;
  ruleResult?: boolean;
  isMandatory?: boolean;
  logic?: LogicInfo[];
  rulesExpression?: string;
  responseItems?: ResponseItem[];
  displayResponseBy?: string;
  seqNo?: number;
}

interface TemplateSection {
  sectionNo?: number;
  itemCode: string;
  caption?: string;
  allowRepeat?: boolean;
  ruleResult?: boolean;
  items: TemplateItem[];
}

interface TemplateItem {
  formattedItemCode?: string;
  itemCode: string;
  fieldType?: string;
  dataSource?: string;
  lookupDependent?: string;
  sectionNo?: number;
  caption?: string;
  helpText?: string;
  isMandatory?: boolean;
  media?: MediaFileInfo[];
  note?: string;
  action?: ActionInfo[];
  responseItems?: ResponseItem[];
  allowMultiSelect?: boolean;
  displayResponseBy?: string;
  rulesExpression?: string;
  ruleResult?: boolean;
  sections?: TemplateSection[];
  items?: TemplateItem[];
  logic?: LogicInfo[];
  seqNo?: number;
  links?: LinkItem[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- template answer is a dynamic JSON value
  ans?: any;
}

interface LookupField {
  formattedItemCode: string;
  dataSource: string;
  lookupDependent: string;
  filterBy: string;
}

interface FileListItem {
  key: string;
  url?: string;
}

interface LinkItem {
  key: string;
  caption?: string;
  url?: string;
}

interface LogicInfo {
  val: string;
  actions: LogicActionItem[];
}

interface LogicActionItem {
  name: string;
  userIds?: string;
  when?: string;
}

interface ResponseItem {
  val: string;
  score?: number | null;
  parent?: string;
}

interface LookupOption {
  id?: number | string;
  name?: string;
  woCode?: string;
  assetId?: string;
  address?: string;
  [key: string]: unknown;
}

/** An answer list with nothing real in it — empty, or only blanks (a multiple choice saved as [""]). */
function isBlankAnswerList(value: unknown[]): boolean {
  return value.every((entry) => entry === null || entry === undefined || `${entry}`.trim() === '');
}

@Component({
  selector: 'app-preview-template',
  templateUrl: './preview-template.component.html',
  styleUrl: './preview-template.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  // eslint-disable-next-line @angular-eslint/prefer-standalone -- declared in NgModule, not yet migrated to standalone
  standalone: false
})
export class PreviewTemplateComponent implements OnInit, AfterViewInit {
  private readonly inspectionDeleteWarning = 'Deleting this Inspection/Task will remove all data and images. Are you sure you want to delete this item?';
  private fb = inject(FormBuilder);
  private loaderService = inject(LoaderService);
  private userPreferencesService = inject(UserPreferencesService);
  private globalService = inject(GlobalService);
  private toastrService = inject(ToastrService);
  private inspectionService = inject(InspectionService);
  private toastService = inject(ToastService);
  private dialog = inject(MatDialog);
  private localStorageService = inject(LocalStorageService);
  private permissionService = inject(PermissionService);
  private workOrderService = inject(WorkOrderService);
  private cdr = inject(ChangeDetectorRef);

  get canCreateAction(): boolean {
    return this.permissionService.canByName('Action', 'create');
  }

  get canEditAction(): boolean {
    return this.permissionService.canByName('Action', 'edit');
  }

  get canDeleteAction(): boolean {
    return this.permissionService.canByName('Action', 'delete');
  }

  userPreferencesData: UserPreferencesData;
  templatePreviewForm: FormGroup;
  checklists: Record<string, ChecklistResponse> = {};
  score = 0;
  get templateFieldType(): typeof FieldType { return FieldType; }
  @Input() totalScore = 0;
  @Input() isPreview = true;
  @Input() templateData: TemplateDataItem[] = [];
  @Input() templateType: string;
  @Input() recordId: number;
  @Input() templateName: string;
  @Input() enableScoring: boolean;
  @Input() titleFormat: string;
  @Input() isSystemForm = false;
  /**
   * The inspection side panel (COL-843): sections fold, the first shown on each page open and the rest
   * closed, with Expand all / Collapse all above them. Off everywhere else, where every section stays
   * open as before.
   */
  @Input() collapsibleSections = false;
  /** Sections the user (or Expand / Collapse all, or validation) has opened or closed. */
  private sectionOpen = new Map<TemplateSection, boolean>();
  @Input() completedOn: string | Date | null;
  /**
   * Answers shown but not changeable: a completed inspection in its side panel until Edit (COL-843).
   * The form is disabled, so nothing auto-saves, and every way to add, change or delete is hidden.
   */
  @Input() set readOnly(value: boolean) {
    this._readOnly = !!value;
    this.applyReadOnly();
  }
  get readOnly(): boolean {
    return this._readOnly;
  }
  private _readOnly = false;
  @Input() linkSummary?: TemplateLinkSummary | null;
  @Output() performAutoUpdate = new EventEmitter<string[]>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix -- legacy @Output naming, preserved for caller API compatibility
  @Output() onCreateAction = new EventEmitter<{ itemInfo: TemplateItem; actionInfo: ActionInfo; sectionNo: number | undefined }>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix -- legacy @Output naming, preserved for caller API compatibility
  @Output() onDeleteAction = new EventEmitter<{
    itemInfo: TemplateItem;
    actionInfo: ActionInfo;
    sectionNo: number | undefined;
    resolve: (result: boolean) => void;
  }>();
  @Output() linkSummaryClick = new EventEmitter<void>();
  @ViewChild('stepper') stepper: MatStepper;
  actionStatus = ActionStatus;
  actionStatusID = ActionStatusID;
  lookUpDataSource: Record<string, LookupOption[]> = {};
  lookupSearchControls: Record<string, FormControl> = {};
  filteredLookups: Record<string, LookupOption[]> = {};
  private lookupDisplayCache: Record<string, { val: unknown; text: string }> = {};
  private filteredOptionsCache = new Map<string, Observable<ResponseItem[]>>();
  dependentLookUpFields: LookupField[] = [];
  notifyFields: string[] = [];
  dataSourceList = {
    "Project": "accountId",
    "WorkPackage": "workPackageId",
    "WorkOrder": "workOrderId",
    "WorkOrderType": "workOrderTypeId",
    "User": "userId",
    "Supervisor": "supervisorId",
    "BootsOnGround": "bootsOnGroundId",
    "Action": "actionId",
    "Schedular": "schedularId",
    "Booking": "bookingId",
    "State": "state",
    "Partner Company": "partnerCompanyId"
  };
  maskPlaceholder = { day: 'dd', month: 'mm', year: 'yyyy', hour: 'hh', minute: 'mm', second: 'ss' };
  lastEditedInfo = '';

  ngOnInit() {
    this.userPreferencesData = this.userPreferencesService.getUserPreferencesData();
    if(this.templateData != undefined && this.templateData.length > 0){
      this.createDynamicForm();
    }
  }

  onLinkSummaryAction(): void {
    if (this.linkSummary?.disabled) {
      return;
    }
    this.linkSummaryClick.emit();
  }

  ngAfterViewInit(): void {
    if(this.isPreview){
      this.initializeEvent();
    }
  }

  get currentPageCaption(): string{
    return this.templateData?.length > 0 ? this.templateData[this.stepper?.selectedIndex ?? 0].caption : '';
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- _event consumed by template binding, param intentionally unused
  onClickPageNavigation(_event: Event){
    document.querySelector(".page-selector .menu-item")?.classList.toggle("hidden");
    document.querySelector(".page-selector .arrowIcon")?.classList.toggle("rotate-180");
  }

  onSelectPage(index){
    this.stepper.selectedIndex = index;
    document.querySelector('.page-selector .menu-item').classList.add("hidden");
    document.querySelector('.page-selector .arrowIcon').classList.remove("rotate-180");
  }

  initializeEvent(){
    document.addEventListener("click", (e) => {
      const ddlPageSelection = document.querySelector(".page-selector .menu-btn");
      const pageMenu = document.querySelector(".page-selector .menu-item");
      const arrow = document.querySelector(".page-selector .arrowIcon");
      if ( pageMenu 
          && !ddlPageSelection?.contains(e.target as Node) 
          && !pageMenu.contains(e.target as Node)
      ) {
        pageMenu.classList.add("hidden");
        arrow.classList.remove("rotate-180");
      }
    });
  }

  loadTemplateData(templateJson: TemplateDataItem[], fileList: FileListItem[]): void {
    this.templateData = templateJson ?? [];
    this.createDynamicForm()
    this.templateData?.forEach(page => {
      page.sections.forEach(section => {
        section.items.forEach(question => {
          if(question.fieldType == FieldType.Instruction){
            if(question.links?.length > 0){
              question.links.filter(x => x.key != '').forEach(linkInfo => {
                linkInfo.url = fileList.find(x => x.key == linkInfo.key)?.url;
              });
            }
          }
          else{
            this.checklists[question.formattedItemCode] = { val: question.ans };
            if(question.fieldType == FieldType.Time){
              //Time answers are stored as "HH:mm"; the timepicker binds to a Date instance.
              this.checklists[question.formattedItemCode].val = this.toTimeDate(question.ans) as unknown as ChecklistResponse['val'];
            }
            if(question.note != undefined){
              this.checklists[question.formattedItemCode].note = question.note;
            }
            if(question.media != undefined){
              this.checklists[question.formattedItemCode].media = question.media;
            }
            if(question.action != undefined){
              this.checklists[question.formattedItemCode].action = question.action;
            }
            if(question.fieldType == FieldType.Image || question.fieldType == FieldType.Media){
              if(this.checklists[question.formattedItemCode].val?.length > 0){
                (this.checklists[question.formattedItemCode].val as MediaFileInfo[]).forEach((fileInfo: MediaFileInfo) => {
                  fileInfo.url = fileList.find(x => x.key == fileInfo.data)?.url;
                });
              }
            }
            //Media
            if(this.checklists[question.formattedItemCode].media?.length > 0){
              this.checklists[question.formattedItemCode].media.forEach(fileInfo => {
                fileInfo.url = fileList.find(x => x.key == fileInfo.data)?.url;
              });
            }
          }

          //Collect logic enabled fields — treat missing "when" as "Immediately" (older data omits the field)
          if(question.logic?.find(logic => logic.actions?.find(action => action.name == 'Notify' && (action.when == null || action.when == undefined || action.when == 'Immediately'))) != undefined){
            this.notifyFields.push(question.formattedItemCode);
          }
        });
      });
    });
    this.initializeEvent();
    this.calculateScore();

    this.templatePreviewForm.valueChanges
    .pipe(
      startWith(this.templatePreviewForm.value), // initial value
      pairwise(),
      debounceTime(2000)
    )
    .subscribe(([previous, current])  => {
      if (this.templatePreviewForm.dirty) {
        const notify: string[] = [];
        if(this.notifyFields?.length > 0){
          const fieldKey = Object.keys(current).find(field => this.notifyFields.includes(field));
          if(fieldKey != undefined && previous[fieldKey] !== current[fieldKey]){
            notify.push(fieldKey);
          }
        }
        if (this.performAutoUpdate) {
          this.performAutoUpdate.emit(notify);
        }
        this.templatePreviewForm.markAsPristine();
      }
    });
  }

  getTemplateDataForSave(): TemplateDataItem[] {
    this.templateData?.forEach(page => {
      page.sections?.forEach(section => {
        section.items?.forEach(question => {
          if (page.ruleResult === false || section.ruleResult === false || question.ruleResult === false) {
            question.ans = null;
          } else if (question.fieldType == FieldType.Time) {
            //Time fields must be persisted as "HH:mm" only — the picker hands back a full Date.
            question.ans = this.toTimeString(this.checklists[question.formattedItemCode].val);
          } else {
            question.ans = this.checklists[question.formattedItemCode].val;
          }
          if(this.checklists[question.formattedItemCode].note != undefined){
            question.note = this.checklists[question.formattedItemCode].note;
          }
          if(this.checklists[question.formattedItemCode].media != undefined){
            question.media = this.checklists[question.formattedItemCode].media;
          }
          if(this.checklists[question.formattedItemCode].action != undefined){
            question.action = this.checklists[question.formattedItemCode].action;
          }
        });
      });
    });
    return this.templateData;
  }

  extractRules(input: string): Rule[] {
    return extractRules(input);
  }

  /** Works out which form control holds the answer for a rule's field (repeated sections get their number appended). */
  private resolveRuleFieldId(field: string, sectionInfo: TemplateSection | null): string {
    let fieldId = field.replace(/\./gi, "");
    if((sectionInfo?.sectionNo ?? 0) > 1
      && field.split('.').slice(0, 2).join('.') == sectionInfo?.itemCode.split('.').slice(0, 2).join('.')){
      fieldId = fieldId + sectionInfo.sectionNo.toString();
    }
    return fieldId;
  }

  checkExpression(itemInfo: TemplateItem | TemplateDataItem, sectionInfo: TemplateSection | null): boolean {
    let isValid = true;
    if(itemInfo.rulesExpression){
      const evaluate = createRuleEvaluator(field =>
        this.templatePreviewForm.get(this.resolveRuleFieldId(field, sectionInfo))?.value);
      isValid = evaluate(itemInfo.rulesExpression);
    }
    itemInfo.ruleResult = isValid;
    if(!isValid){
      this.resetValidationErrors(itemInfo);
      this.clearHiddenAnswers(itemInfo);
    }
    return isValid;
  }

  private clearHiddenAnswers(itemInfo: TemplateItem | TemplateDataItem): void {
    const pageCode: string = itemInfo.itemCode.split('.')[0];
    if (itemInfo.fieldType == FieldType.Page) {
      this.templateData?.filter(x => x.itemCode == pageCode).forEach(pageInfo => {
        pageInfo?.sections.forEach(sectionInfo => {
          sectionInfo?.items.forEach(item => {
            if (this.checklists[item.formattedItemCode]) {
              this.checklists[item.formattedItemCode].val = null;
            }
          });
        });
      });
    } else if (itemInfo.fieldType == FieldType.Section) {
      const sectionCode: string = pageCode + '.' + itemInfo.itemCode.split('.')[1];
      this.templateData?.filter(x => x.itemCode == pageCode).forEach(pageInfo => {
        pageInfo?.sections.filter(x => x.itemCode == sectionCode).forEach(sectionInfo => {
          sectionInfo?.items.forEach(item => {
            if (this.checklists[item.formattedItemCode]) {
              this.checklists[item.formattedItemCode].val = null;
            }
          });
        });
      });
    } else {
      const code = (itemInfo as TemplateItem).formattedItemCode;
      if (code && this.checklists[code]) {
        this.checklists[code].val = null;
      }
    }
  }

  resetValidationErrors(itemInfo: TemplateItem): void {
    const pageCode: string = itemInfo.itemCode.split(".")[0];
    if(itemInfo.fieldType == FieldType.Page){
      this.templateData?.filter(x => x.itemCode == pageCode).forEach((pageInfo) => {
        pageInfo?.sections.forEach((sectionInfo) => {
          sectionInfo?.items.forEach((item) => {
            this.templatePreviewForm.get(item.formattedItemCode)?.setErrors(null);
            this.templatePreviewForm.get(item.formattedItemCode)?.markAsUntouched();
          });
        });
      });
    }
    else if(itemInfo.fieldType == FieldType.Section){
      const sectionCode: string = pageCode + "." + itemInfo.itemCode.split(".")[1];
      this.templateData?.filter(x => x.itemCode == pageCode).forEach((pageInfo) => {
        pageInfo?.sections.filter(x => x.itemCode == sectionCode).forEach((sectionInfo) => {
          sectionInfo?.items.forEach((item) => {
            this.templatePreviewForm.get(item.formattedItemCode)?.setErrors(null);
            this.templatePreviewForm.get(item.formattedItemCode)?.markAsUntouched();
          });
        });
      });
    }
    else{
      this.templatePreviewForm.get(itemInfo.formattedItemCode)?.setErrors(null);
      this.templatePreviewForm.get(itemInfo.formattedItemCode)?.markAsUntouched();
    }
  }

  createDynamicForm(){
    const replaceChar = /\./gi;
    this.filteredOptionsCache.clear();
    this.templatePreviewForm = new FormGroup({});
    const items = this.getAllItemsInOrder(this.templateData);

    this.templateData?.forEach((pageInfo) => {
      // When the backend doesn't preserve sectionNo, repeated sections all arrive with
      // sectionNo = undefined, producing identical formattedItemCodes and a checklists
      // overwrite. Assign sectionNo by occurrence order for any section that appears
      // more than once on this page.
      const sectionCounter = new Map<string, number>();
      pageInfo?.sections.forEach(sectionInfo => {
        const count = (sectionCounter.get(sectionInfo.itemCode) ?? 0) + 1;
        sectionCounter.set(sectionInfo.itemCode, count);
        if (count > 1) {
          sectionInfo.sectionNo = count;
        }
      });

      pageInfo?.sections.forEach((sectionInfo) => {
        sectionInfo?.items.forEach((itemInfo) => {

          itemInfo.formattedItemCode = itemInfo.itemCode.replace(replaceChar, "") + (sectionInfo.sectionNo ?? '');
          this.addControlToForm(itemInfo);

          if(itemInfo.fieldType == FieldType.LookUp){
            this.initLookupField(itemInfo, items);
          }
        });
        this.linkStartEndTimes(sectionInfo);
      });
    });

    this.templatePreviewForm.updateValueAndValidity();
    this.applyReadOnly();
  }

  /**
   * A Time question captioned "… End …" or "… Finish …" must be later than the one in its section
   * captioned the same with "Start" (e.g. Estimated Finish Time after Estimated Start Time). Templates
   * have no rules across questions, so the pair is found by caption; other Time questions are left as
   * they are (COL-842).
   */
  private linkStartEndTimes(sectionInfo: TemplateSection): void {
    const times = (sectionInfo?.items ?? []).filter(item => item.fieldType == FieldType.Time);
    const endWord = /\b(end|finish)\b/i;
    const startWord = /\bstart\b/i;
    const pairKey = (caption: string | undefined, word: RegExp) => (caption ?? '').trim().toLowerCase().replace(word, '\u0000');
    times.forEach(end => {
      if (!endWord.test(end.caption ?? '')) return;
      const endKey = pairKey(end.caption, endWord);
      const start = times.find(item => item !== end && startWord.test(item.caption ?? '') && pairKey(item.caption, startWord) === endKey);
      const endControl = this.templatePreviewForm.get(end.formattedItemCode);
      const startControl = start ? this.templatePreviewForm.get(start.formattedItemCode) : null;
      if (!endControl || !startControl) return;
      endControl.addValidators(this.endAfterStartValidator(startControl));
      // A new start time can make the end time valid or invalid.
      startControl.valueChanges.subscribe(() => endControl.updateValueAndValidity());
    });
  }

  private endAfterStartValidator(startControl: AbstractControl): ValidatorFn {
    return (endControl: AbstractControl): ValidationErrors | null => {
      const start = this.toTimeString(startControl.value);
      const end = this.toTimeString(endControl.value);
      // "HH:mm" strings compare in time order.
      return start && end && end <= start ? { endBeforeStart: true } : null;
    };
  }

  private applyReadOnly(): void {
    if (!this.templatePreviewForm) return;
    if (this._readOnly) {
      this.templatePreviewForm.disable({ emitEvent: false });
    } else if (this.templatePreviewForm.disabled) {
      this.templatePreviewForm.enable({ emitEvent: false });
    }
  }

  async initLookupField(itemInfo: TemplateItem, items: TemplateItem[]): Promise<void> {
    const replaceChar = /\./gi;

    if(itemInfo.lookupDependent && itemInfo.lookupDependent.length > 0){
      const lookUpField: LookupField = {
        formattedItemCode: itemInfo.formattedItemCode,
        dataSource: itemInfo.dataSource,
        lookupDependent: itemInfo.lookupDependent,
        filterBy: items.find(x => x.itemCode == itemInfo.lookupDependent).dataSource
      };
      this.dependentLookUpFields.push(lookUpField);

      const filterColumn = this.dataSourceList[lookUpField.filterBy];
      this.templatePreviewForm.get(lookUpField.lookupDependent.replace(replaceChar, ""))
        .valueChanges
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe(value => {
          this.filteredLookups[lookUpField.formattedItemCode] = this.lookUpDataSource[lookUpField.dataSource]
                ?.filter(x => x[filterColumn] == value);
        });
    }

    if(itemInfo.dataSource === AppModuleName.WorkOrder){
      // WorkOrder uses lazy server-side search via openLookupDialog — no eager fetch needed.
      // But for edit mode: if a WO ID is already saved, resolve its label now so the field
      // and title don't display the raw numeric ID before the user re-opens the picker.
      const code = itemInfo.formattedItemCode;
      const existingVal = itemInfo.ans ?? this.checklists[code]?.val;
      if (existingVal != null && existingVal !== '') {
        this.workOrderService.getWorkOrderDetails(Number(existingVal)).subscribe({
          next: detail => {
            if (detail?.woCode) {
              this.lookupDisplayCache[code] = {
                val: existingVal,
                text: `${detail.woCode ?? ''} (Asset ID: ${detail.assetId ?? ''})`
              };
              this.cdr.markForCheck();
            }
          }
        });
      }
      return;
    }

    if(this.lookUpDataSource[itemInfo.dataSource] == undefined){
      if(itemInfo.dataSource == 'State'){
        this.lookUpDataSource[itemInfo.dataSource] = this.globalService.getAustraliaState();
      }
      else{
        this.lookUpDataSource[itemInfo.dataSource] = await firstValueFrom(this.globalService.getByModule(itemInfo.dataSource, this.userPreferencesData?.selectedCompanyId, this.userPreferencesData?.userId,  this.localStorageService.getItem('partnerCompanyId') || 0)) as unknown as LookupOption[];
      }
      this.initLookupControl(itemInfo);
      this.cdr.detectChanges();
    }
  }

  initLookupControl(itemInfo: TemplateItem): void {
    const replaceChar = /\./gi;
    const control = new FormControl('');
    this.lookupSearchControls[itemInfo.formattedItemCode] = control;
    if(itemInfo.dataSource == AppModuleName.Supervisor){
      this.filteredLookups[itemInfo.formattedItemCode] = [...new Map(this.lookUpDataSource[itemInfo.dataSource].map(x => [x.name, x])).values()];
    }
    else{
      this.filteredLookups[itemInfo.formattedItemCode] = [...this.lookUpDataSource[itemInfo.dataSource]];
    }

    control.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(search => {
        const lookUpField = this.dependentLookUpFields.find(x => x.lookupDependent == itemInfo.itemCode);
        if(lookUpField == undefined){
          if(itemInfo.dataSource == AppModuleName.Supervisor){
            const uniqueList: LookupOption[] = [...new Map(this.lookUpDataSource[itemInfo.dataSource].map(x => [x.name, x])).values()];

            this.filteredLookups[itemInfo.formattedItemCode] = !search
              ? [...uniqueList]
              : uniqueList.filter(option => option.name.toLowerCase().includes(search.toLowerCase()));
          }
          else if(itemInfo.dataSource == AppModuleName.WorkOrder){
            this.filteredLookups[itemInfo.formattedItemCode] = !search
              ? [...this.lookUpDataSource[itemInfo.dataSource]]
              : this.lookUpDataSource[itemInfo.dataSource]
                .filter(option => option.woCode.toLowerCase().includes(search.toLowerCase())
                      || option.assetId.includes(search)
                      || option.address.toLowerCase().includes(search.toLowerCase()));
          }
          else{
            this.filteredLookups[itemInfo.formattedItemCode] = !search
              ? [...this.lookUpDataSource[itemInfo.dataSource]]
              : this.lookUpDataSource[itemInfo.dataSource]
                .filter(option => option.name.toLowerCase().includes(search.toLowerCase()));
          }
        }
        else{
          const filterColumn = this.dataSourceList[lookUpField.filterBy];
          const data = this.lookUpDataSource[lookUpField.dataSource].filter(x => x[filterColumn] == this.checklists[lookUpField.lookupDependent.replace(replaceChar, "")]);

          this.filteredLookups[itemInfo.formattedItemCode] = !search
            ? data
            : data.filter(option => option.name.toLowerCase().includes(search.toLowerCase()));
        }
      });
  }

  getAllItemsInOrder(formJson: TemplateDataItem[]): TemplateItem[] {
      const items: TemplateItem[] = [];

      formJson.forEach(page => {
          page.sections?.forEach(section => {
          const sortedItems = [...(section.items || [])]
              .sort((a, b) => a.seqNo - b.seqNo);

            items.push(...sortedItems);
          });
      });

      return items;
  }

  addControlToForm(itemInfo: TemplateItem): void {
    this.checklists[itemInfo.formattedItemCode] = { val: null };
    this.templatePreviewForm.addControl(
      itemInfo?.formattedItemCode,
      this.fb.control("")
    );
    this.setValidation(itemInfo);
  };

  removeControlFromForm(itemInfo: TemplateItem): void {
    delete this.checklists[itemInfo.formattedItemCode];
    this.templatePreviewForm.removeControl(itemInfo?.formattedItemCode);
  };

  setValidation(itemInfo: TemplateItem): void {
    let validators: ValidatorFn[] = [];
    const control = this.templatePreviewForm.get(itemInfo.formattedItemCode);

    // Adding required validator if the field is mandatory
    if (itemInfo.isMandatory) {
      validators = [this.mandatoryValidator(itemInfo)];
    }

    if(itemInfo.logic && itemInfo.logic.length > 0){
      validators.push(this.fieldCustomValidator());
    }

    // Adding field-specific validators based on field type
    if (![FieldType.Page, FieldType.Section].includes(itemInfo.fieldType as FieldType)) {
      // Adding email validator
      if(itemInfo.fieldType == FieldType.Email){
        validators.unshift(Validators.email);
      }
      control.setValidators(validators);
    }
  }

  mandatoryValidator(itemInfo: TemplateItem): ValidatorFn {
      return (control: AbstractControl): Record<string, boolean> | null => {
          const [pageCode, sectionCodeBase, , extra] = itemInfo.itemCode.split('.');
          const sectionCode = `${pageCode}.${sectionCodeBase}${extra ? '.' + extra : ''}`
          const page = this.templateData.find(x => x.itemCode == pageCode);
          const section = page.sections.find(x => x.itemCode == sectionCode);
          
          if(page.ruleResult && section.ruleResult && itemInfo.ruleResult){
            const value = control.value;
            if( value == null 
              || (typeof value === 'string' && value.trim() === '') 
              || (Array.isArray(value) && isBlankAnswerList(value))
            ){
                if([FieldType.Image, FieldType.Signature, FieldType.Media].includes(itemInfo.fieldType as FieldType)){
                  if(this.checklists[itemInfo.formattedItemCode] == undefined || (this.checklists[itemInfo.formattedItemCode].val?.length??0) == 0){
                    return { 'required': true };
                  }
                }
                else{
                  return { 'required': true };
                }
            }
          }
          return null;
      };
  }

  fieldCustomValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let errorMessage = '';
      const formattedItemCode = this.getControlName(control);
      const itemInfo = this.getTemplateItem(formattedItemCode);
      if(itemInfo != undefined && itemInfo.logic?.length > 0){
        itemInfo.logic.forEach(logic => {
          if(this.matchesLogicValue(control.value, logic.val)){
            let isValid = true;
            if(logic.actions.find(x => x.name == LogicAction.Note) && !this.checklists[formattedItemCode]?.note?.length){
              isValid = false;
            }
            else if(logic.actions.find(x => x.name == LogicAction.Media) && !this.checklists[formattedItemCode]?.media?.length){
              isValid = false;
            }
            else if(logic.actions.find(x => x.name == LogicAction.Action) && !this.checklists[formattedItemCode]?.action?.length){
              isValid = false;
            }

            if(!isValid){
              const actions = this.formatWithAnd(logic.actions);
              if(['[Empty]', '[Not Empty]', '[Selected]', '[Not Selected]'].includes(logic.val)){
                errorMessage += `<span>${actions} required when value is ${logic.val.replace('[', '').replace(']', '').toLowerCase()}</span>`;
              }
              else{
                errorMessage += `<span>${actions} required when "${logic.val}" is selected</span>`;
              }
            }
          }
        });
      }
      return errorMessage != '' ? { custom: { message: errorMessage } } : null;
    };
  }

  getTemplateItem(formattedItemCode: string): TemplateItem | undefined {
    if (!this.templateData) return undefined;
    const replaceChar = /\./gi;
    for (const page of this.templateData) {
      for (const section of page.sections || []) {
        const item = section.items?.find(a => a.itemCode.replace(replaceChar, '') == formattedItemCode);
        if (item) return item;
      }
    }
    return undefined;
  }

  getControlName(control: AbstractControl | null): string | null {
    if (!control || !control.parent) return null;
    const parent = control.parent;
    return Object.keys(parent.controls).find(name => control === parent.controls[name]) || null;
  }

  matchesLogicValue(controlValue: string | string[] | null, logicValue: string): boolean {
    if (Array.isArray(controlValue)) {
      if(logicValue == '[Not Selected]'){
        return controlValue.length === 0;
      }
      else if(logicValue == '[Selected]'){
        return controlValue.length > 0;
      }
      else{
        return controlValue.map(v => String(v)).includes(String(logicValue));
      }
    }
    else{
      const val = controlValue?.toString().trim() || "";
      if (logicValue === "[Not Selected]" || logicValue === "[Empty]") {
        return val.length === 0;
      }
      else if (logicValue === "[Selected]" || logicValue === "[Not Empty]") {
        return val.length > 0;
      }
      else{
        return String(val) === String(logicValue);
      }
    }
  }

  formatWithAnd(values: LogicActionItem[]): string {
    if (!values || values.length === 0) return '';
    if (values.length === 1) return values[0].name + ' is ';
    if (values.length === 2) return `${values[0].name} and ${values[1].name} are `;

    const allExceptLast = values.map(x => x.name).slice(0, -1).join(', ');
    const last = values[values.length - 1].name;
    return `${allExceptLast}, and ${last} are `;
  }

  /**
   * COL-624: a section like "Penetrations Photos" is a separate section from "Penetrations",
   * linked only by a rulesExpression on one of its items referencing a Penetrations field
   * (e.g. `([P1.S3.Q1] EqualsTo [Yes])`). Repeating just `sectionInfo` left the paired section
   * as a single, never-repeated instance shared by every conceptual Penetrations instance —
   * which is what produced "Add doesn't create a paired Photos section" and, once the
   * inspection was saved/reloaded, "the new instance shows the previous instance's photos"
   * (there was only ever one real Photos record for loadTemplateData() to hydrate from).
   *
   * Returns the itemCodes of sections whose rulesExpression depends on sectionInfo's Page.Section.
   */
  private getDependentSectionItemCodes(pageInfo: TemplateDataItem, sectionInfo: TemplateSection): string[] {
    const prefix = sectionInfo.itemCode.split('.').slice(0, 2).join('.');
    const dependentItemCodes = new Set<string>();
    pageInfo.sections
      .filter(other => other.itemCode !== sectionInfo.itemCode)
      .forEach(other => {
        const isDependent = other.items.some(item =>
          item.rulesExpression && this.extractRules(item.rulesExpression).some(rule =>
            rule.field.split('.').slice(0, 2).join('.') === prefix
          )
        );
        if (isDependent) {
          dependentItemCodes.add(other.itemCode);
        }
      });
    return [...dependentItemCodes];
  }

  addRepeatativeSection(pageInfo: TemplateDataItem, sectionInfo: TemplateSection): void {
    if (this.readOnly) return;
    const dependentItemCodes = this.getDependentSectionItemCodes(pageInfo, sectionInfo);
    // One structural template per dependent section type — all of that type's existing
    // instances share the same item definitions, only their answer data differs.
    const dependentTemplates = dependentItemCodes
      .map(itemCode => pageInfo.sections.find(x => x.itemCode === itemCode))
      .filter((x): x is TemplateSection => x != undefined);

    // Insert the whole new group (primary + paired) as one contiguous block right after the
    // LAST existing section belonging to this repeat-group, in natural template order (e.g.
    // Penetrations then Penetrations Photos) — inserting each clone right after its own
    // source section instead would scatter the new pair (new Photos ending up BEFORE the
    // original Photos section), which also scrambles sectionNo numbering on reload, since
    // createDynamicForm() assigns sectionNo by array position.
    const groupItemCodes = new Set([sectionInfo.itemCode, ...dependentItemCodes]);
    let insertAt = pageInfo.sections.reduce(
      (lastIndex, section, index) => groupItemCodes.has(section.itemCode) ? index : lastIndex,
      -1
    ) + 1;

    [sectionInfo, ...dependentTemplates].forEach(sourceSection => {
      const newSection: TemplateSection = JSON.parse(JSON.stringify(sourceSection));
      newSection.sectionNo = pageInfo.sections.filter(x => x.itemCode == sourceSection.itemCode).length + 1;

      // A brand-new repeated instance must start blank — carrying over the source section's
      // live answers here is exactly what caused the "photos copied in" symptom.
      newSection.items.forEach(itemInfo => {
        itemInfo.ans = null;
        itemInfo.note = undefined;
        itemInfo.media = [];
        itemInfo.action = [];
      });

      pageInfo.sections.splice(insertAt, 0, newSection);
      // A copy the user just added is the one they're about to fill in.
      this.sectionOpen.set(newSection, true);
      insertAt++;

      newSection.items.forEach(itemInfo => {
        itemInfo.formattedItemCode = itemInfo.itemCode.replace(/\./gi, "") + newSection.sectionNo;
        this.addControlToForm(itemInfo);
      });
      this.linkStartEndTimes(newSection);
    });

    this.templatePreviewForm.updateValueAndValidity();

    if(this.performAutoUpdate != undefined){
      this.performAutoUpdate.emit([]);
    }
  }

  displayRepeatSectionButton(pageInfo: TemplateDataItem, sectionInfo: TemplateSection): boolean {
    // eslint-disable-next-line no-useless-assignment -- explicit false init required before conditional branching
    let allowDisplay = false;
    if(sectionInfo.allowRepeat == false){
      allowDisplay = false;
    }
    else{
      const sectionInx = pageInfo.sections.indexOf(sectionInfo) + 1;
      const totalSection = pageInfo.sections.length;
      if(sectionInx == totalSection){
        allowDisplay = true;
      }
      else{
        allowDisplay = sectionInfo.itemCode.split(".")[1] != pageInfo.sections[sectionInx].itemCode.split(".")[1];
      }
    }

    if(allowDisplay){
      allowDisplay = this.checkExpression(sectionInfo, sectionInfo);
    }

    return allowDisplay;
  }

  onClickDeleteSection(pageInfo: TemplateDataItem, sectionInfo: TemplateSection): void {
    if (this.readOnly) return;
    const dialogData: CommonConfirmationDialog = {
        buttons: [
        { name: 'Cancel', class: '!text-base', color: 'accent' },
        { name: 'Delete', class: '!text-base', handler: () => { this.deleteSection(pageInfo, sectionInfo); } }
        ],
        confirmMsgDescription: "Are you sure, you want to delete this section?"
    };
        
    this.dialog.open(ConfirmDialogComponent, {
        maxWidth: '500px', width: '90%', panelClass: 'cmn-dialog', data: dialogData
    });
  }

  deleteSection(pageInfo: TemplateDataItem, sectionInfo: TemplateSection): void {
    // COL-624: deleting a repeated Penetrations instance must also delete its paired Photos
    // instance (matched by the same sectionNo) — otherwise repeated deletes leave orphaned
    // dependent sections with no matching primary instance.
    const dependentItemCodes = this.getDependentSectionItemCodes(pageInfo, sectionInfo);
    const pairedInstances = pageInfo.sections.filter(other =>
      dependentItemCodes.includes(other.itemCode) && (other.sectionNo ?? 1) === (sectionInfo.sectionNo ?? 1)
    );

    [sectionInfo, ...pairedInstances].forEach(section => this.deleteSingleSection(pageInfo, section));

    if(this.performAutoUpdate != undefined){
      this.performAutoUpdate.emit([]);
    }
  }

  private deleteSingleSection(pageInfo: TemplateDataItem, sectionInfo: TemplateSection): void {
    const request: UploadMediaRequest = {
      recordId: this.recordId,
      moduleName: AppModuleName.Inspection,
      referenceId: '0',
      referenceTable: Constants.MediaType.Media,
      itemCode: '',
      file: undefined,
      mediaKey: '',
      action: UploadOperation.Delete,
      companyId: this.userPreferencesData.selectedCompanyId,
      uploadBy: this.userPreferencesData.userId,
      uploadedByName: this.userPreferencesData.userName
    };

    sectionInfo.items.forEach(itemInfo => {
      if(itemInfo.fieldType == FieldType.Image || itemInfo.fieldType == FieldType.Media){
        itemInfo.ans?.forEach(media => {
          request.mediaKey = media.data;
          request.sectionNo = sectionInfo.sectionNo;
          this.globalService.uploadMedia(request);
        });
      }

      itemInfo.media?.forEach(media => {
        request.mediaKey = media.data;
        request.sectionNo = sectionInfo.sectionNo;
        this.globalService.uploadMedia(request);
      });

      this.removeControlFromForm(itemInfo);
    });
    this.templatePreviewForm.updateValueAndValidity();

    pageInfo.sections.splice(pageInfo.sections.indexOf(sectionInfo), 1);

    pageInfo.sections.filter(x => x.itemCode == sectionInfo.itemCode).forEach((x, i) => {
      x.sectionNo = i + 1;
    });
  }

  openDrawingBoard(itemInfo: TemplateItem): void {
    if (this.readOnly) return;
    const dialogRef = this.dialog.open(DrawingPadComponent, {
        maxWidth: '500px', width: '90%',
        data: { pad_height: 100, pad_width: 200, pad_data: this.checklists[itemInfo.formattedItemCode].val },
        panelClass: 'cmn-dialog'
    });
    dialogRef.afterClosed().subscribe((shouldReload) => {
      if(shouldReload){
        this.checklists[itemInfo.formattedItemCode].val = dialogRef.componentInstance.signatureImg??"";
        this.templatePreviewForm.get(itemInfo.formattedItemCode)?.setValue(dialogRef.componentInstance.signatureImg??"");
      }
    });
  }

  validatePage(pageInfo: TemplateDataItem): void {
    let isValid = true;
    pageInfo.sections.forEach(section =>{
      section.items.forEach(item => {
        this.templatePreviewForm.get(item.formattedItemCode)?.markAsTouched();
        isValid = isValid && this.templatePreviewForm.get(item.formattedItemCode)?.valid;
      });
    });
    if(isValid){
      this.stepper.next();
    }
  }

  // Validates every page (not just the one currently in view), since Submit/Complete must
  // catch required fields anywhere in the template. When something's invalid, jumps the
  // stepper to the first offending page — otherwise the error badges render on a page the
  // user isn't looking at and the click appears to silently do nothing.
  /** A page's name for people: its caption, or "Page N" when the template left it untitled. */
  pageLabel(pageInfo: TemplateDataItem, index: number): string {
    const caption = (pageInfo?.caption ?? '').trim();
    return caption && !/^untitled page$/i.test(caption) ? caption : `Page ${index + 1}`;
  }

  /** The page the stepper is on (0 before it exists). */
  get currentPageIndex(): number {
    return this.stepper?.selectedIndex ?? 0;
  }

  /** The panel's page navigator: previous / next / a numbered page from the menu. */
  goToPage(index: number): void {
    if (!this.stepper || index < 0 || index >= (this.templateData?.length ?? 0)) return;
    this.stepper.selectedIndex = index;
  }

  /**
   * A section's name for people: its caption, or "Section N" (its place among the page's shown
   * sections) when the template left it untitled — with the copy number for a repeatable section.
   */
  sectionLabel(pageInfo: TemplateDataItem, sectionInfo: TemplateSection): string {
    const name = this.sectionName(pageInfo, sectionInfo);
    return sectionInfo?.allowRepeat ? `${name} · ${sectionInfo.sectionNo ?? 1}` : name;
  }

  /** sectionLabel without the copy number — for "Add another …". */
  sectionName(pageInfo: TemplateDataItem, sectionInfo: TemplateSection): string {
    const caption = (sectionInfo?.caption ?? '').trim();
    if (caption && !/^untitled section$/i.test(caption)) return caption;
    // Its place among the page's shown sections, copies of a repeatable one counted once.
    const kinds = [...new Set((pageInfo?.sections ?? []).filter((s) => this.checkExpression(s, s)).map((s) => s.itemCode))];
    return `Section ${kinds.indexOf(sectionInfo.itemCode) + 1}`;
  }

  /** A section's progress, for its folded header: questions shown, answered, and required still missing. */
  sectionProgress(sectionInfo: TemplateSection): { total: number; answered: number; missing: number } {
    let total = 0;
    let answered = 0;
    let missing = 0;
    for (const item of sectionInfo?.items ?? []) {
      const control = this.templatePreviewForm?.get(item.formattedItemCode);
      if (!control || !this.checkExpression(item, sectionInfo)) continue;
      total++;
      const value = this.checklists?.[item.formattedItemCode]?.val ?? control.value;
      const hasValue = Array.isArray(value) ? !isBlankAnswerList(value) : value !== null && value !== undefined && `${value}`.trim() !== '';
      if (hasValue) answered++;
      else if (item.isMandatory) missing++;
    }
    return { total, answered, missing };
  }

  /** Whether a section is open: always, unless sections fold (collapsibleSections). */
  isSectionExpanded(pageInfo: TemplateDataItem, sectionInfo: TemplateSection): boolean {
    if (!this.collapsibleSections) return true;
    const chosen = this.sectionOpen.get(sectionInfo);
    if (chosen !== undefined) return chosen;
    return this.firstShownSection(pageInfo) === sectionInfo;
  }

  /** The panel's own toggle: remembered, so change detection doesn't fold it back. */
  onSectionToggled(sectionInfo: TemplateSection, open: boolean): void {
    if (this.collapsibleSections) this.sectionOpen.set(sectionInfo, open);
  }

  /** Expand all / Collapse all: every section on every page. */
  setAllSectionsExpanded(open: boolean): void {
    for (const page of this.templateData ?? []) {
      for (const section of page.sections ?? []) this.sectionOpen.set(section, open);
    }
  }

  /** Whether every shown section on every page is open (true) or closed (false) — to disable the button that would do nothing. */
  allSectionsAre(open: boolean): boolean {
    return (this.templateData ?? []).every((page) =>
      !this.checkExpression(page, null) || (page.sections ?? []).every((section) =>
        !this.checkExpression(section, section) || this.isSectionExpanded(page, section) === open));
  }

  /** The first section a page actually shows (its conditions allowing). */
  private firstShownSection(pageInfo: TemplateDataItem): TemplateSection | undefined {
    return (pageInfo.sections ?? []).find((section) => this.checkExpression(section, section));
  }

  validateForm(): boolean {
    if (!this.templateData) return true;
    let isValid = true;
    let firstInvalidPageIndex: number | null = null;
    this.templateData.forEach((page, pageIndex) => {
      page.sections.forEach(section => {
        section.items.forEach(item => {
          const control = this.templatePreviewForm.get(item.formattedItemCode);
          control?.markAsTouched();
          control?.updateValueAndValidity();
          if (control && !control.valid) {
            isValid = false;
            if (firstInvalidPageIndex === null) firstInvalidPageIndex = pageIndex;
            // A folded section must not hide what it's missing.
            if (this.collapsibleSections) this.sectionOpen.set(section, true);
          }
        });
      });
    });
    if (!isValid && firstInvalidPageIndex !== null && this.stepper) {
      this.stepper.selectedIndex = firstInvalidPageIndex;
    }
    return isValid;
  }

  onClickNote(itemInfo: TemplateItem): void {
    if (this.readOnly) return;
    const dialogRef = this.dialog.open(CreateNoteComponent, {
      maxWidth: '500px', width: '90%', 
      data: { note: this.checklists[itemInfo.formattedItemCode]["note"] },
      panelClass: 'cmn-dialog'
    });
    dialogRef.afterClosed().subscribe((shouldReload) => {
      if(shouldReload){
        const note: string = dialogRef.componentInstance.createNoteForm.get("note").value??"";
        this.checklists[itemInfo.formattedItemCode]["note"] = note;
        //itemInfo.note = note;
        this.templatePreviewForm.get(itemInfo.formattedItemCode)?.updateValueAndValidity();
        if(this.performAutoUpdate != undefined){
          this.performAutoUpdate.emit([]);
        }
      }
    });
  }

  onClickMedia(itemInfo: TemplateItem, sectionNo: number | undefined): void {
    if (this.readOnly) return;
    const dialogRef = this.dialog.open(CreateMediaComponent, {
      maxWidth: '800px', width: '90%',
      data: {
        itemInfo: itemInfo,
        checklists: this.checklists,
        panelClass: 'cmn-dialog',
        templateType: this.templateType,
        recordId: this.recordId,
        sectionNo: sectionNo
      }
    });
    dialogRef.afterClosed().subscribe((response) => {
      if(response?.shouldReload){
        this.templatePreviewForm.get(itemInfo.formattedItemCode)?.updateValueAndValidity();
        this.cdr.detectChanges();
      }
    });
  }

  async selectImages(event: Event, itemInfo: TemplateItem, sectionNo: number | undefined): Promise<void> {
    if (this.readOnly) return;
    const input = event.target as HTMLInputElement;
    const selectedFiles = input.files;

    if(this.checklists[itemInfo.formattedItemCode].val?.length == 0){
      this.checklists[itemInfo.formattedItemCode].val = [];
    }

    if (selectedFiles && selectedFiles?.length > 0) {
      await Promise.all(Array.from(selectedFiles).map(async file => {
        if(this.recordId > 0){
          this.uploadMedia(itemInfo, Constants.MediaType.ImageField, file, sectionNo);
        }
        else{
          const mediaInfo: MediaFileInfo = {
            caption: file.name,
            data: '',
            url: ''
          };
          (this.checklists[itemInfo.formattedItemCode].val as MediaFileInfo[]).push(mediaInfo);
        }
      }));

      this.templatePreviewForm.get(itemInfo.formattedItemCode)?.setValue(this.checklists[itemInfo.formattedItemCode].val);
      (event.target as HTMLInputElement).value = '';

      this.loaderService.hideLoader();
    }
  }

  uploadMedia(itemInfo: TemplateItem, mediaType: string, file: File, sectionNo: number | undefined): void {
    const request: UploadMediaRequest = {
      recordId: this.recordId,
      moduleName: this.templateType,
      referenceId: '0',
      referenceTable: mediaType,
      itemCode: itemInfo.itemCode,
      sectionNo: sectionNo,
      file: file,
      mediaKey: '',
      action: UploadOperation.Upload,
      companyId: this.userPreferencesData.selectedCompanyId,
      uploadBy: this.userPreferencesData.userId,
      uploadedByName: this.userPreferencesData.userName
    };

    if(this.checklists[itemInfo.formattedItemCode].val == undefined){
      this.checklists[itemInfo.formattedItemCode].val = [];
    }

    this.loaderService.showLoader('Uploading...');
    this.globalService.uploadMedia(request).subscribe({
      next: (response) => {
        if (response?.success) {
          const mediaInfo = { 
            caption: file.name, 
            data: response.mediaKey, 
            uploadDate: new Date().toISOString(),
            uploadedByName: this.userPreferencesData?.userName,
            url: response.imageUrl
          };
          (this.checklists[itemInfo.formattedItemCode].val as MediaFileInfo[]).push(mediaInfo);
          this.toastrService.success('Uploaded successfully!');
        }
        else{
          this.toastrService.error('Failed to upload');
        }
        this.loaderService.hideLoader();
      },
      error: (err) => {
        this.loaderService.hideLoader();
        this.toastrService.error('Something went wrong during upload');
        console.error('Error while uploading:', err);
      }
    });
  }

  getDynamicTitle(): string{
    if(this.checklists == undefined || Object.keys(this.checklists).length == 0
      || this.titleFormat == undefined || this.titleFormat.length == 0){
      return '';
    }
    else{
      const replaceChar = /\./gi;
      let title = '';
      const titleParts = this.titleFormat.split("|");
      titleParts.forEach(titlePart => {
        if(titlePart == 'name'){
          title += this.templateName + " | ";
        }
        else{
          const formattedCode = titlePart.replace(replaceChar, "");
          let val = this.checklists[formattedCode].val;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dynamic template data structure from API
          const item = this.templateData?.flatMap(p => p.sections?.flatMap((s: any) => s.items) ?? []).find((q: any) => q.formattedItemCode == formattedCode);
          if(item?.fieldType == FieldType.LookUp && (val ?? '') != ''){
            val = this.getLookupDisplayText(item, val);
          }
            if (!Array.isArray(val) && this.isValidDate(val)) {
            title += moment(val).format('DD MMM YYYY') + " | ";
          }
          else if((val??'') != ''){
            title += val + " | ";
          }
        }
      }); 
      return title.slice(0, -3);
    }
  }

  getLookupDisplayValue(itemInfo: TemplateItem): string {
    const code = itemInfo.formattedItemCode;
    const val = this.checklists[code]?.val;
    if (val == null || val === '') {
      return '';
    }
    if (itemInfo.dataSource === AppModuleName.Supervisor) {
      return String(val);
    }
    const cached = this.lookupDisplayCache[code];
    if (cached && cached.val === val) {
      return cached.text;
    }
    const source = this.lookUpDataSource[itemInfo.dataSource];
    if (!source) {
      return '';
    }
    let text: string;
    if (itemInfo.dataSource === AppModuleName.WorkOrder) {
      const option = source.find(x => x.id == val);
      text = option ? `${option.woCode} (Asset ID: ${option.assetId}) ${option.address ?? ''}` : String(val);
    }
    else {
      const option = source.find(x => x.id == val);
      text = option?.name ?? String(val);
    }
    this.lookupDisplayCache[code] = { val, text };
    return text;
  }

  openLookupDialog(itemInfo: TemplateItem): void {
    if (this.readOnly) return;
    const code = itemInfo.formattedItemCode;
    if (!this.checklists[code]) return;

    // WorkOrder: open with server-side searchFn; no pre-loaded data required
    if (itemInfo.dataSource === AppModuleName.WorkOrder) {
      const companyId = this.userPreferencesData?.selectedCompanyId ?? 0;
      const cached = this.lookupDisplayCache[code];
      const selectedOption: LookupSelectOption | undefined =
        (cached?.val != null && cached.val !== '')
          ? { value: cached.val as string | number, label: cached.text, searchText: cached.text.toLowerCase() }
          : undefined;
      const dialogRef = this.dialog.open(LookupSelectDialogComponent, {
        data: {
          title: 'Select ' + (itemInfo.caption ?? ''),
          options: [],
          selectedValue: this.checklists[code]?.val,
          selectedOption,
          searchFn: (search: string, page: number) =>
            this.workOrderService.getWorkOrderListForDropdown(companyId, search, page).pipe(
              map(list => list.map(x => ({
                value: x.id,
                label: `${x.woCode ?? ''} (Asset ID: ${x.assetId ?? ''})`,
                subLabel: `${x.street ?? ''}, ${x.city ?? ''}, ${x.state ?? ''}, ${x.country ?? ''}-${x.postCode ?? ''}`,
                searchText: `${x.woCode ?? ''} ${x.assetId ?? ''}`.toLowerCase()
              } as LookupSelectOption)))
            )
        } as LookupSelectDialogData,
        width: '900px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        panelClass: 'lookup-select-dialog-container',
        autoFocus: false
      });
      dialogRef.afterClosed().subscribe((result: LookupSelectOption | null) => {
        const control = this.templatePreviewForm.get(code);
        control?.markAsTouched();
        if (!result || !this.checklists[code]) return;
        this.checklists[code].val = result.value as string;
        control?.setValue(result.value);
        control?.markAsDirty();
        // Cache the display text so getLookupDisplayValue shows the label without a data source
        this.lookupDisplayCache[code] = { val: result.value, text: result.label };
      });
      return;
    }

    if (!this.filteredLookups[code] && !this.lookUpDataSource[itemInfo.dataSource]) return;
    const source = this.filteredLookups[code] ?? this.lookUpDataSource[itemInfo.dataSource] ?? [];

    let options: LookupSelectOption[];
    if (itemInfo.dataSource === AppModuleName.Supervisor) {
      options = source.map(x => ({
        value: x.name,
        label: x.name,
        searchText: (x.name ?? '').toLowerCase()
      }));
    }
    else {
      options = source.map(x => ({
        value: x.id,
        label: x.name,
        searchText: (x.name ?? '').toLowerCase()
      }));
    }

    const dialogRef = this.dialog.open(LookupSelectDialogComponent, {
      data: {
        title: 'Select ' + (itemInfo.caption ?? ''),
        options: options,
        selectedValue: this.checklists[code]?.val,
        isLoading: this.lookUpDataSource[itemInfo.dataSource] == undefined
      } as LookupSelectDialogData,
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      panelClass: 'lookup-select-dialog-container',
      autoFocus: false
    });

    dialogRef.afterClosed().subscribe((result: LookupSelectOption | null) => {
      const control = this.templatePreviewForm.get(code);
      control?.markAsTouched();
      if (!result || !this.checklists[code]) {
        return;
      }
      this.checklists[code].val = result.value as string;
      control?.setValue(result.value);
      control?.markAsDirty();
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dynamic template item and answer types from API
  private getLookupDisplayText(itemInfo: any, val: any): string {
    const source = this.lookUpDataSource[itemInfo.dataSource];
    if (!source) {
      // For WO fields, the source is never eagerly loaded; check the display cache
      // (populated on init or after the user picks from the dialog).
      if (itemInfo.dataSource === AppModuleName.WorkOrder) {
        const cached = this.lookupDisplayCache[itemInfo.formattedItemCode];
        if (cached && cached.val == val) return cached.text;
      }
      return val;
    }
    if (itemInfo.dataSource === AppModuleName.Supervisor) {
      return val;
    } else if (itemInfo.dataSource === AppModuleName.WorkOrder) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dynamic lookup data source entries
      const option = source.find((x: any) => x.id == val);
      return option ? `${option.woCode} (Asset ID: ${option.assetId})` : val;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dynamic lookup data source entries
      const option = source.find((x: any) => x.id == val);
      return option?.name ?? val;
    }
  }

  formatTitle(){
    if (this.readOnly) return;
    const dialogRef = this.dialog.open(FormatTitlePopupComponent, {
      width: '700px',
      data: { templateId: this.recordId, templateData: this.templateData, titleFormat: this.titleFormat },
      panelClass: 'cmn-dialog',
    });

    dialogRef.afterClosed().subscribe((response) => {
      if(response != undefined && response.titleFormat != undefined){
        if(this.recordId > 0){
          const request = { 
            inspectionId: this.recordId,
            titleFormat: response.titleFormat,
            userId: this.userPreferencesData.userId
          };
          this.inspectionService.updateTitleFormat(request).subscribe({
            next: () => {
              this.titleFormat = response.titleFormat;
              this.loaderService.hideLoader();
              this.toastService.showSuccess('Title Format updated successfully.');
            },
            error: (err: HttpErrorResponse) => {
              console.error(err);
              this.loaderService.hideLoader();
              this.toastService.showError('Error while updating title format.');
            }
          });
        }
      }
    });
  }

  calculateScore(): void {
    let currentScore : number | null;
    if(this.enableScoring){
      currentScore = 0;
      for (const page of (this.templateData ?? [])) {
        for (const section of page.sections || []) {
          for (const question of section.items || []) {
            if([FieldType.MultipleChoice, FieldType.DropDown].includes(question.fieldType as FieldType)) {
              const selectedAns = question.responseItems?.find(x => x.val == this.checklists[question.formattedItemCode].val);
              if(selectedAns != undefined){
                currentScore += selectedAns.score ?? 0;
              }
            }
          }
        }
      }
    }
    this.score = currentScore;
  }

  openActionPopup(itemInfo: TemplateItem, actionData: ActionInfo, sectionNo: number | undefined): void {
    if (this.readOnly) return;
    if(this.recordId == 0){
      this.toastService.showWarning('Please first save document then you can able to create action');
      return;
    }
    if (!actionData && !this.canCreateAction) {
      this.toastService.showWarning('You do not have permission to create actions.');
      return;
    }
    if (actionData && !this.canEditAction) {
      this.toastService.showWarning('You do not have permission to edit actions.');
      return;
    }
    const isCreate = (actionData?.id ?? 0) == 0;
    const dialogRef = this.dialog.open(CreateActionComponent, {
      data: {
        actionId: actionData?.id ?? 0,
        actionInfo: null,
        currentModule: this.templateType,
        currentRecordId: this.recordId,
        subItemId: itemInfo.itemCode,
        itemCaption: itemInfo.caption,
        allowLinking: false
      },
      width: '600px',
      panelClass: 'cmn-action-dialog',
      hasBackdrop: true,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((response) => {
      if(response.success){
        if(this.checklists[itemInfo.formattedItemCode].action == undefined){
          this.checklists[itemInfo.formattedItemCode].action = [];
        }

        const actionInfo: ActionInfo = {
          caption: response.data.caption,
          id: response.data.id,
          status: response.data.status
        };

        const action = this.checklists[itemInfo.formattedItemCode].action.find(x => x.id == response.data.id);
        if(action != undefined){
          action.caption = response.data.caption;
          action.status = response.data.status;
        }
        else{
          this.checklists[itemInfo.formattedItemCode].action.push(actionInfo);
        }

        this.templatePreviewForm.get(itemInfo.formattedItemCode)?.updateValueAndValidity();

        if(this.onCreateAction != undefined && isCreate){
          this.onCreateAction.emit({ itemInfo, actionInfo, sectionNo });
        }
      }
    });
  }

  onDeleteActionClick(itemInfo: TemplateItem, actionInfo: ActionInfo, sectionNo: number | undefined): void {
    if (this.readOnly) return;
    if (!this.canDeleteAction) {
      this.toastService.showWarning('You do not have permission to delete actions.');
      return;
    }
    const dialogData: CommonConfirmationDialog = {
      confirmMsg: 'Confirm Delete',
      confirmMsgDescription: this.inspectionDeleteWarning,
      buttons: [
        { name: 'Cancel', class: '!text-base', color: 'accent' },
        { name: 'Delete', class: '!text-base', handler: () => { this.deleteAction(itemInfo, actionInfo, sectionNo); } },
      ],
    };

    const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: '500px', width: '90%', panelClass: 'cmn-dialog', data: dialogData,
    });

    confirmDialogRef.afterClosed().subscribe({
      // eslint-disable-next-line @typescript-eslint/no-empty-function -- intentional no-op; only error path needs handling
      next: () => { },
      error: (err: HttpErrorResponse) => { console.error('err: ', err); },
    });
  }

  deleteAction(itemInfo: TemplateItem, actionInfo: ActionInfo, sectionNo: number | undefined): void {
    this.loaderService.showLoader('Deleting action details..');
    new Promise<boolean>((resolve) => {
      this.onDeleteAction.emit({ itemInfo, actionInfo, sectionNo, resolve });
    })
    .then((isDeleted) => {
      if (isDeleted) {
        this.checklists[itemInfo.formattedItemCode].action = this.checklists[itemInfo.formattedItemCode].action.filter(x => x.id != actionInfo.id);
        this.cdr.detectChanges();
        this.toastService.showSuccess('Action deleted successfully');
      } else {
        this.toastService.showError('Error while deleting action');
      }
    })
    .finally(() => {
      this.loaderService.hideLoader();
    });
  }

  /**
   * Convert a stored Time answer into the Date the material timepicker binds to.
   * Accepts the "HH:mm" format we persist plus the full date-time strings written by
   * older builds (and by the mobile app before the same fix landed).
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- template answer type is dynamic
  toTimeDate(val: any): Date | null {
    if (val instanceof Date) {
      return Number.isNaN(val.getTime()) ? null : val;
    }
    if (typeof val !== 'string' || val.trim() === '') {
      return null;
    }
    const timeOnly = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(val.trim());
    const date = new Date();
    if (timeOnly) {
      const hours = Number(timeOnly[1]);
      const minutes = Number(timeOnly[2]);
      if (hours > 23 || minutes > 59) {
        return null;
      }
      date.setHours(hours, minutes, Number(timeOnly[3] ?? 0), 0);
      return date;
    }
    const parsed = new Date(val);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  /** Format a Time answer as "HH:mm"; empty answers stay empty. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- template answer type is dynamic
  toTimeString(val: any): string | null {
    if (val == null || val === '') {
      return null;
    }
    const date = this.toTimeDate(val);
    if (date == null) {
      return null;
    }
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }

  isValidDate(val: string | number | null | undefined): boolean {
    if (val == null || val === '') return false;
    if (typeof val === 'number') return false;

    const d = new Date(val);
    return !Number.isNaN(d.getTime());
  }

  getSectionScore(sectionInfo: TemplateSection): string {
    if(this.enableScoring){
      let sectionTotalScore = 0;
      let sectionScore  = 0;
      for (const question of sectionInfo.items || []) {
        if([FieldType.MultipleChoice, FieldType.DropDown].includes(question.fieldType as FieldType)) {
          sectionTotalScore += Math.max(...question.responseItems.map(x => (x.score ?? 0)));
          const selectedAns = question.responseItems?.find(x => x.val == this.checklists[question.formattedItemCode].val);
          if(selectedAns != undefined){
            sectionScore += selectedAns.score ?? 0;
          }
        }
      }
      return `${sectionScore}/${sectionTotalScore} (${(((sectionScore ?? 0) / (sectionTotalScore || 1)) * 100).toFixed(2)}%)`;
    }
    else{
      return '';
    }
  }

  getFilteredOptions$(itemInfo: TemplateItem): Observable<ResponseItem[]> {
    const replaceChar = /\./gi;
    const formattedItemCode = itemInfo.displayResponseBy.replace(replaceChar, "");
    if (!this.filteredOptionsCache.has(formattedItemCode)) {
      this.filteredOptionsCache.set(
        formattedItemCode,
        this.templatePreviewForm.get(formattedItemCode)!.valueChanges.pipe(
          startWith(this.templatePreviewForm.get(formattedItemCode)!.value),
          map(value =>
            value ? itemInfo.responseItems.filter(i => i.parent.includes(value)) : itemInfo.responseItems
          )
        )
      );
    }
    return this.filteredOptionsCache.get(formattedItemCode)!;
  }

  getLinks(links: LinkItem[], returnLink: boolean): LinkItem[] {
    return (returnLink ? links?.filter(x => x.caption == '') : links?.filter(x => x.caption != '')) ?? [];
  }

  getFileUrl(mediaKey: string, fileUrl: string): string {
    // eslint-disable-next-line no-useless-assignment -- explicit empty init required before switch statement
    let url = '';
    switch(mediaKey.split('.').pop().toLowerCase()){
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
        case 'bmp':
            url = fileUrl;
            break;
        case 'xls':
        case 'xlsx':
        case 'csv':
            url = 'assets/icons/excel.svg';
            break;
        case 'pdf':
            url = 'assets/icons/download_pdf.svg';
            break;
        default:
            url = 'assets/icons/fir-icon.svg';
            break;
    }
    return url;
  }

  fillAddressDetails(place: google.maps.places.PlaceResult, fieldCode: string): void {
    const fullAddress = place.formatted_address ?? place.name ?? '';
    this.checklists[fieldCode].val = fullAddress;
    this.templatePreviewForm.get(fieldCode)?.setValue(fullAddress);
  }
}
